// The knowledge check as the review screen shows it: the version's question set, which the API sends as
// its Markdown verbatim (view.ts), read for display in the ADR-023 §1 grammar —
//
//   ### Segment 1 · <label>
//   1. <prompt>
//   - a. <option>
//   - b. **<the right option>** ✓
//   Why: <reason> (Guide <pages>)
//
// This is a reader, not the validator: @waypoint/content's parseQuestions decides whether a set is
// valid, and submit refuses one that is not (E31-S2-T3), so a version in review has passed it. A set
// this reader cannot follow is shown as its text instead (readQuestions answers undefined).

export interface ShownOption {
  readonly id: string;
  readonly text: string;
  readonly right: boolean;
}

export interface ShownQuestion {
  readonly prompt: string;
  readonly options: readonly ShownOption[];
  readonly why: string;
  /** The Why line's guide pages, as written (p.4, pp.4–5). */
  readonly pages: string | undefined;
}

export interface ShownSegment {
  readonly n: number;
  readonly label: string;
  readonly questions: readonly ShownQuestion[];
}

const SEGMENT = /^###\s+Segment\s+(\d+)\s*·\s*(.*?)\s*$/;
const QUESTION = /^\s*\d+\.\s+(.+)$/;
const OPTION = /^\s*-\s+([A-Za-z])\.\s+(.+)$/;
const RIGHT = /^\*\*(.+?)\*\*\s*✓$/;
const WHY = /^\s*Why:\s*(.*)$/;
const CITED = /^(.*?)\s*\(Guide\s+([^()]*?)\s*\)$/;
const BEAT = /^\s*Beat:/;

interface Draft {
  prompt: string;
  options: ShownOption[];
  why?: string;
  pages?: string;
}

export function readQuestions(markdown: string): ShownSegment[] | undefined {
  const segments: { n: number; label: string; questions: Draft[] }[] = [];
  for (const raw of markdown.split(/\r?\n/)) {
    if (raw.trim() === "" || BEAT.test(raw)) continue;
    const seg = SEGMENT.exec(raw);
    if (seg) {
      segments.push({ n: Number(seg[1]), label: seg[2] ?? "", questions: [] });
      continue;
    }
    const segment = segments.at(-1);
    if (segment === undefined) return undefined;
    const question = QUESTION.exec(raw);
    if (question) {
      segment.questions.push({ prompt: (question[1] ?? "").trim(), options: [] });
      continue;
    }
    const current = segment.questions.at(-1);
    if (current === undefined) return undefined;
    const option = OPTION.exec(raw);
    if (option) {
      const text = (option[2] ?? "").trim();
      const right = RIGHT.exec(text);
      current.options.push({
        id: (option[1] ?? "").toLowerCase(),
        text: right ? (right[1] ?? "").trim() : text,
        right: right !== null,
      });
      continue;
    }
    const why = WHY.exec(raw);
    if (why) {
      const cited = CITED.exec((why[1] ?? "").trim());
      current.why = cited ? (cited[1] ?? "") : (why[1] ?? "").trim();
      if (cited) current.pages = cited[2] ?? "";
      continue;
    }
    return undefined;
  }
  if (segments.length === 0) return undefined;
  const complete = segments.every((s) =>
    s.questions.every((q) => q.options.length > 0 && q.why !== undefined),
  );
  if (!complete) return undefined;
  return segments.map((s) => ({
    n: s.n,
    label: s.label,
    questions: s.questions.map((q) => ({
      prompt: q.prompt,
      options: q.options,
      why: q.why ?? "",
      pages: q.pages,
    })),
  }));
}
